import { SortDir } from "./SortItem";

export type TableSortState<SortBy extends string> = {
  sortBy: SortBy;
  sortDir: SortDir;
};

export const getNextTableSort = <SortBy extends string>(
  current: TableSortState<SortBy>,
  value: SortBy,
  defaultDir: (value: SortBy) => SortDir
): TableSortState<SortBy> => ({
  sortBy: value,
  sortDir:
    current.sortBy === value
      ? current.sortDir === SortDir.Asc
        ? SortDir.Desc
        : SortDir.Asc
      : defaultDir(value),
});

export const applySortDir = (result: number, sortDir: SortDir): number =>
  sortDir === SortDir.Asc ? result : -result;

export const compareStrings = (
  a: string,
  b: string,
  sortDir: SortDir = SortDir.Asc
): number => applySortDir(a.localeCompare(b), sortDir);

export const compareNumbers = (
  a: number,
  b: number,
  sortDir: SortDir = SortDir.Asc
): number => applySortDir(a - b, sortDir);

export const compareBigInts = (
  a: bigint,
  b: bigint,
  sortDir: SortDir = SortDir.Asc
): number => applySortDir(a < b ? -1 : a > b ? 1 : 0, sortDir);

export const normalizeBigIntDecimals = (
  amount: string | bigint,
  decimals: number,
  targetDecimals = 18
): bigint => {
  const value = BigInt(amount);
  return decimals <= targetDecimals
    ? value * 10n ** BigInt(targetDecimals - decimals)
    : value / 10n ** BigInt(decimals - targetDecimals);
};

export const compareNullableValues = <T>(
  a: T | null | undefined,
  b: T | null | undefined,
  sortDir: SortDir,
  compare: (a: T, b: T) => number
): number => {
  // Missing financial values stay at the bottom in both sort directions.
  if (a === null || a === undefined) {
    return b === null || b === undefined ? 0 : 1;
  }
  if (b === null || b === undefined) return -1;

  return applySortDir(compare(a, b), sortDir);
};

export const compareNullableNumbers = (
  a: number | null | undefined,
  b: number | null | undefined,
  sortDir: SortDir
): number => compareNullableValues(a, b, sortDir, (x, y) => x - y);

export const compareNullableBigInts = (
  a: bigint | null | undefined,
  b: bigint | null | undefined,
  sortDir: SortDir
): number =>
  compareNullableValues(a, b, sortDir, (x, y) => (x < y ? -1 : x > y ? 1 : 0));

export const compareNullableDates = (
  a: string | number | Date | null | undefined,
  b: string | number | Date | null | undefined,
  sortDir: SortDir
): number => {
  const aTime = a === null || a === undefined ? null : new Date(a).getTime();
  const bTime = b === null || b === undefined ? null : new Date(b).getTime();

  return compareNullableNumbers(
    aTime === null || Number.isNaN(aTime) ? null : aTime,
    bTime === null || Number.isNaN(bTime) ? null : bTime,
    sortDir
  );
};
